import React from 'react';
import {
  Mail, MessageSquare, Tag as TagIcon, User, Clock,
  Brain, TrendingDown, TrendingUp, Minus, ShieldAlert, Zap
} from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { PriorityBadge } from './PriorityBadge';
import { Ticket } from '../../services/api';

export type { Ticket } from '../../services/api';

const CATEGORY_SHORT: Record<string, string> = {
  ShippingDelivery: 'Shipping',
  ReturnsCancellationsExchanges: 'Returns',
  PaymentBilling: 'Billing',
  ProductInfoAvailability: 'Product',
  CustomerComplaintsSatisfaction: 'Complaint',
  AccountProfileOther: 'Account',
  PromotionsDiscounts: 'Promos',
  TechnicalSupport: 'Tech Support',
  GeneralInquiry: 'General',
};

interface TicketListItemProps {
  ticket: Ticket;
  selected?: boolean;
  onClick?: () => void;
}

export function TicketListItem({ ticket, selected, onClick }: TicketListItemProps) {
  const isSnoozed = ticket.snoozed_until && new Date(ticket.snoozed_until) > new Date();

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return '1d';
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div
      onClick={onClick}
      className={`
        border-b border-gray-100 px-4 py-3 cursor-pointer transition-all duration-100
        ${selected
          ? 'bg-amber-50/60 border-l-2 border-l-amber-500'
          : 'border-l-2 border-l-transparent hover:bg-gray-50/80'
        }
      `}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="flex-1 min-w-0">
          <h3 className={`text-[13px] font-semibold truncate ${selected ? 'text-gray-900' : 'text-gray-700'}`}>
            {ticket.subject || 'No Subject'}
          </h3>

          <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
            <User size={11} className="text-gray-400" />
            <span className="truncate">
              {ticket.customer_name || ticket.customer_email}
            </span>
            {ticket.message_count > 1 && (
              <>
                <span className="text-gray-300">·</span>
                <MessageSquare size={11} className="text-gray-400" />
                <span>{ticket.message_count}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-[11px] text-gray-400 tabular-nums">
            {formatTimeAgo(ticket.updated_at || ticket.created_at)}
          </span>
          {isSnoozed && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-medium">
              <Clock size={10} />
              Snoozed
            </span>
          )}
        </div>
      </div>

      {/* Preview Text */}
      {ticket.preview && (
        <p className="text-xs text-gray-500 mb-2 line-clamp-1">
          {ticket.preview}
        </p>
      )}

      {/* AI + Status Row */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* AI Category — primary signal */}
        {ticket.category && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/10">
            <Zap size={9} />
            {CATEGORY_SHORT[ticket.category] || ticket.category.replace(/([A-Z])/g, ' $1').trim()}
          </span>
        )}

        {/* Sentiment indicator */}
        {ticket.sentiment && (
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-medium ${
            ticket.sentiment === 'negative'
              ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10'
              : ticket.sentiment === 'positive'
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/10'
                : 'bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-500/10'
          }`}>
            {ticket.sentiment === 'negative' && <TrendingDown size={9} />}
            {ticket.sentiment === 'positive' && <TrendingUp size={9} />}
            {ticket.sentiment === 'neutral' && <Minus size={9} />}
            {ticket.sentiment.charAt(0).toUpperCase() + ticket.sentiment.slice(1)}
          </span>
        )}

        {/* Human review flag */}
        {ticket.requires_human_review && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/15">
            <ShieldAlert size={9} />
            Review
          </span>
        )}

        {/* Status + Priority (secondary) */}
        <StatusBadge status={ticket.status} size="sm" />
        {ticket.priority !== 'normal' && (
          <PriorityBadge priority={ticket.priority} size="sm" />
        )}

        {ticket.channel && ticket.channel !== 'email' && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] bg-gray-50 text-gray-600 border border-gray-200/80">
            <Mail size={9} />
            {ticket.channel}
          </span>
        )}

        {/* Tags — compact, only show if no AI category */}
        {!ticket.category && ticket.tags && ticket.tags.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {ticket.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10"
              >
                <TagIcon size={9} />
                {tag}
              </span>
            ))}
            {ticket.tags.length > 2 && (
              <span className="text-[10px] text-gray-400">
                +{ticket.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
